import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { useInRouterContext, useNavigate } from 'react-router-dom';
import {
  BREAKDOWN_PANEL_CATEGORIES,
  ELEMENT_CATEGORIES,
  type ElementCategory,
  type SceneCoverage,
  type SceneDetailView,
  type SceneItemView,
  type TextRange,
} from '@scoutaround/script-breakdown-shared';

import { snapToWords } from '../../lib/snapToWords.js';
import { SceneConversationEntry } from '../conversations/ConversationDrawer.js';
import { Icon } from '../Icon.js';
import { MoreMenu, type MenuItem } from '../ui/MoreMenu.js';
import { StatusPill } from '../ui/StatusPill.js';
import { CoverageMark } from './CoverageMark.js';
import { useSceneBreakdown } from './context.js';
import {
  ITEM_STATE_LABEL,
  PROGRESS_LABEL,
  PROGRESS_STATE,
  evidenceLabel,
  pageLength,
  sceneName,
} from './labels.js';
import { MarkKey } from './MarkKey.js';
import {
  itemMarks,
  lineSegments,
  markPaint,
  markRuns,
  markStyleOf,
  screenplayLines,
  type ItemMark,
  type MarkRun,
} from './marks.js';
import { TagPopover, type TagScope } from './TagPopover.js';
import { PREFERENCE_KEYS, parseBoolean, serialiseBoolean, usePreference } from './storage.js';

/** A request to put ranges of the scene in view and pulse them; a new nonce is a new request. */
export interface ReaderFlash {
  readonly target: 'TEXT' | 'HEADING';
  readonly ranges: readonly TextRange[];
  readonly nonce: number;
}

/**
 * Whether the open scene's AI reading is worth offering again, ONE SCENE AT A TIME: read before the
 * scene changed, read in part, not read, not analysed, or with a category whose request failed. A
 * fully (or selectively) read scene is not offered a paid re-read, and a legacy reading with no
 * recorded coverage is not either — nothing says what it missed.
 */
export function sceneRereadOffered(detail: Pick<SceneDetailView, 'coverage' | 'categories'>): boolean {
  return REREAD_COVERAGE.has(detail.coverage) || detail.categories.some((c) => c.coverage === 'FAILED');
}

const REREAD_COVERAGE: ReadonlySet<SceneCoverage> = new Set<SceneCoverage>(['OUT_OF_DATE', 'PARTIAL', 'NOT_READ', 'NOT_ANALYSED']);

const FONT_STEPS = [12.5, 13.5, 14.5, 16, 17.5] as const;
const DEFAULT_FONT_STEP = 2;
const FLASH = '__flash';

/**
 * THE SCREENPLAY, THE SOURCE OF TRUTH — read-only, with what the breakdown says marked where the
 * server located it.
 *
 * A MARK IS EVIDENCE, DRAWN AS WHAT WAS VERIFIED (rule 3): filled for an item on the sheet at a
 * verified quote, outlined for one awaiting a decision, dashed where the quote was found elsewhere
 * or appears more than once (every occurrence, none chosen), and a dotted underline for an
 * approximate name match. Where items overlap one is drawn and the mark lists them all. Clicking a
 * mark opens its item on the right; selecting words tags them. The key under the chips (`MarkKey`)
 * says what each shape means, because nothing on the mark itself does.
 *
 * The scene's place, its length and the script's version are one line; its review state is a
 * separate pill beside it, never folded into the version ("Script v7 (Reviewed)" says two things
 * at once and gets one of them wrong).
 */
export function ScreenplayReader({
  versionNumber,
  prevLabel,
  nextLabel,
  onPrev,
  onNext,
  focus,
  onFocus,
  flash,
  onPickItem,
  onTag,
}: {
  versionNumber: number | null;
  /** The neighbours in the list's order, named; null at an end. */
  prevLabel: string | null;
  nextLabel: string | null;
  onPrev: () => void;
  onNext: () => void;
  focus: boolean;
  onFocus: (on: boolean) => void;
  flash: ReaderFlash | null;
  /** A mark was picked: open its item on the right. */
  onPickItem: (key: string) => void;
  onTag: (text: string, category: ElementCategory, scope: TagScope) => void;
}) {
  const { projectId, detail, detailLoading, detailError, retryDetail, selectedItem, refusal, readScene } = useSceneBreakdown();
  const [category, setCategory] = useState<ElementCategory | null>(null);
  const [highlights, setHighlights] = usePreference(PREFERENCE_KEYS.highlights, true, parseBoolean, serialiseBoolean);
  const [keyOpen, setKeyOpen] = usePreference(PREFERENCE_KEYS.markKey, true, parseBoolean, serialiseBoolean);
  const [fontStep, setFontStep] = usePreference(
    PREFERENCE_KEYS.fontStep,
    DEFAULT_FONT_STEP,
    (raw) => {
      const n = Number(raw);
      return Number.isInteger(n) && n >= 0 && n < FONT_STEPS.length ? n : null;
    },
  );
  const [overlap, setOverlap] = useState<{ keys: readonly string[]; top: number; left: number } | null>(null);
  const [tagging, setTagging] = useState<{ text: string; top: number; left: number } | null>(null);
  const [pulse, setPulse] = useState<ReaderFlash | null>(null);
  const frame = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const scroller = useRef<HTMLDivElement>(null);

  const sceneId = detail?.scene.id ?? null;
  const text = detail?.scene.text ?? '';
  const heading = detail?.scene.slugline ?? '';

  // A new scene starts at its top, with nothing open over it.
  useEffect(() => {
    setOverlap(null);
    setTagging(null);
    setPulse(null);
    if (scroller.current !== null) scroller.current.scrollTop = 0;
  }, [sceneId]);

  // A flash request pulses for a moment; its own effect scrolls it into view.
  useEffect(() => {
    if (flash === null) return;
    setPulse(flash);
    const timer = window.setTimeout(() => {
      setPulse((current) => (current?.nonce === flash.nonce ? null : current));
    }, 1800);

    return () => {
      window.clearTimeout(timer);
    };
  }, [flash]);

  // A quote located nowhere in the scene (a plan's span the text no longer holds) is never marked
  // by a guess: the scene is put in view from its heading instead.
  useEffect(() => {
    if (pulse === null) return;
    const node = frame.current?.querySelector<HTMLElement>('[data-flash="1"]');
    if (node !== null && node !== undefined && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'center', behavior: 'smooth' });
    } else if ((node === null || node === undefined) && scroller.current !== null) {
      scroller.current.scrollTop = 0;
    }
  }, [pulse]);

  const items = useMemo(() => detail?.items ?? [], [detail]);
  const byKey = useMemo(() => new Map(items.map((i) => [i.key, i])), [items]);
  const lines = useMemo(() => screenplayLines(text), [text]);

  // THE MARKS, computed once per scene and filter — not per render.
  const marks = useMemo(
    () =>
      highlights
        ? itemMarks(items, { textLength: text.length, headingLength: heading.length, category, selectedKey: selectedItem })
        : { text: [] as ItemMark[], heading: [] as ItemMark[] },
    [items, text.length, heading.length, category, selectedItem, highlights],
  );

  const withFlash = useCallback(
    (list: readonly ItemMark[], target: 'TEXT' | 'HEADING', length: number): ItemMark[] => {
      if (pulse === null || pulse.target !== target) return [...list];
      const extra = pulse.ranges
        .map((r) => ({ start: Math.max(0, Math.min(r.start, length)), end: Math.max(0, Math.min(r.end, length)) }))
        .filter((r) => r.end > r.start)
        .map((r): ItemMark => ({ key: FLASH, category: 'CAST', style: 'DOTTED', ...r }));

      return [...list, ...extra];
    },
    [pulse],
  );

  const textRuns = useMemo(
    () => markRuns(withFlash(marks.text, 'TEXT', text.length), selectedItem),
    [marks.text, withFlash, text.length, selectedItem],
  );
  const headingRuns = useMemo(
    () => markRuns(withFlash(marks.heading, 'HEADING', heading.length), selectedItem),
    [marks.heading, withFlash, heading.length, selectedItem],
  );

  /** The words selected in the text, grown to whole words, open the tagger under them. */
  const captureSelection = useCallback(() => {
    const selection = window.getSelection();
    const node = body.current;
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed || node === null) return;

    const range = selection.getRangeAt(0);
    if (!node.contains(range.commonAncestorContainer)) return;

    const upToStart = range.cloneRange();
    upToStart.selectNodeContents(node);
    upToStart.setEnd(range.startContainer, range.startOffset);
    const full = node.textContent ?? '';
    const start = upToStart.toString().length;
    const snapped = snapToWords(full, start, start + range.toString().length);
    if (snapped === '') return;

    const box = frame.current?.getBoundingClientRect();
    const rect = typeof range.getBoundingClientRect === 'function' ? range.getBoundingClientRect() : null;
    setOverlap(null);
    setTagging({
      text: snapped,
      top: box && rect ? rect.bottom - box.top + 6 : 120,
      left: box && rect ? Math.max(8, Math.min(rect.left - box.left, box.width - 360)) : 12,
    });
  }, []);

  const pickRun = (run: MarkRun, event: MouseEvent<HTMLElement>) => {
    // The end of a drag over a mark is a selection, not a pick.
    const selection = window.getSelection();
    if (selection !== null && !selection.isCollapsed) return;
    const keys = run.keys.filter((k) => k !== FLASH);
    if (keys.length === 0) return;
    if (keys.length === 1) {
      setOverlap(null);
      onPickItem(keys[0] as string);
      return;
    }
    const box = frame.current?.getBoundingClientRect();
    const rect = event.currentTarget.getBoundingClientRect();
    setTagging(null);
    setOverlap({
      keys,
      top: box ? rect.bottom - box.top + 6 : 120,
      left: box ? Math.max(8, Math.min(rect.left - box.left, box.width - 300)) : 12,
    });
  };

  const renderRuns = (source: string, segments: { start: number; end: number; run: MarkRun | null }[]): ReactNode =>
    segments.map((segment) => {
      const words = source.slice(segment.start, segment.end);
      if (segment.run === null) return <span key={segment.start}>{words}</span>;

      return (
        <MarkSpan
          key={segment.start}
          run={segment.run}
          words={words}
          items={byKey}
          selectedKey={selectedItem}
          onPick={pickRun}
        />
      );
    });

  if (detailError !== null) {
    return (
      <div className="sr" data-testid="sr">
        <div className="el-error" role="alert" data-testid="sr-error">
          <span>{detailError}</span>
          <button type="button" className="ctl" onClick={retryDetail}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (detail === null || detailLoading) {
    return (
      <div className="sr" data-testid="sr">
        <div className="el-skeleton sr-skeleton" role="status" aria-live="polite" data-testid="sr-loading">
          <span className="sr-only">Loading the scene…</span>
          {[60, 92, 80, 40, 88, 70, 30, 84].map((w, i) => (
            <span key={i} className="el-skeleton-row" style={{ width: `${String(w)}%` }} aria-hidden="true" />
          ))}
        </div>
      </div>
    );
  }

  const scene = detail.scene;
  // The open scene alone, read again — offered where its reading is out of date or incomplete.
  const reread = readScene !== null && sceneRereadOffered(detail) ? readScene : null;
  const rereadLabel = detail.coverage === 'NOT_ANALYSED' ? 'Read this scene with AI' : 'Read this scene again with AI';
  const presentCategories = BREAKDOWN_PANEL_CATEGORIES.filter((c) => (detail.counts.byCategory[c] ?? 0) > 0);
  const facts = [pageLength(scene.pageEighths), versionNumber === null ? null : `Script v${String(versionNumber)}`].filter(
    (f): f is string => f !== null,
  );

  return (
    <div className="sr" ref={frame} data-testid="sr" data-scene={scene.id}>
      <header className="sr-head">
        <div className="sr-head-top">
          <p className="sr-pos" data-testid="sr-position">
            {`Scene ${String(detail.position.index)} of ${String(detail.position.total)}`}
          </p>
          <div className="sr-tools">
            <button
              type="button"
              className="ctl sr-tool"
              data-testid="sr-prev"
              disabled={prevLabel === null}
              aria-label={prevLabel === null ? 'No previous scene' : `Previous: ${prevLabel}`}
              title={prevLabel ?? undefined}
              onClick={onPrev}
            >
              <Icon name="chevronLeft" size={16} />
            </button>
            <button
              type="button"
              className="ctl sr-tool"
              data-testid="sr-next"
              disabled={nextLabel === null}
              aria-label={nextLabel === null ? 'No next scene' : `Next: ${nextLabel}`}
              title={nextLabel ?? undefined}
              onClick={onNext}
            >
              <Icon name="chevronRight" size={16} />
            </button>
            <button
              type="button"
              className="ctl sr-tool"
              data-testid="sr-focus"
              aria-pressed={focus}
              aria-label={focus ? 'Show the side panels' : 'Focus on the script'}
              title={focus ? 'Show the side panels' : 'Focus on the script'}
              onClick={() => {
                onFocus(!focus);
              }}
            >
              <Icon name={focus ? 'shrink' : 'expand'} size={15} />
            </button>
            <SceneConversationEntry projectId={projectId} sceneId={scene.id} label={`${sceneName(scene)}${scene.slugline ? ` — ${scene.slugline}` : ''}`} />
            <ReaderMenu
              projectId={projectId}
              sceneId={scene.id}
              extra={
                reread === null
                  ? []
                  : [
                      {
                        id: 'reread',
                        label: rereadLabel,
                        hint: 'A paid reading of this scene alone, never run for you',
                        testId: 'sr-reread-menu',
                        onSelect: reread,
                      },
                    ]
              }
            />
          </div>
        </div>

        <div className="sr-title-line">
          <span className="sr-num" data-testid="sr-number" aria-label={sceneName(scene)}>
            {scene.sceneNumber ?? String(scene.ordinal)}
          </span>
          <h2 className="sr-title" data-testid="sr-heading">
            {heading === ''
              ? 'Untitled scene'
              : renderRuns(heading, lineSegments({ start: 0, end: heading.length }, headingRuns))}
          </h2>
        </div>

        <div className="sr-meta">
          <span data-testid="sr-facts">{facts.join(' · ')}</span>
          <StatusPill
            state={PROGRESS_STATE[detail.progress]}
            label={PROGRESS_LABEL[detail.progress]}
            size="sm"
            testId="sr-review-state"
          />
          <CoverageMark coverage={detail.coverage} withLabel testId="sr-coverage" />
          {reread === null ? null : (
            <button type="button" className="el-link" data-testid="sr-reread" onClick={reread}>
              {rereadLabel}
            </button>
          )}
        </div>

        {presentCategories.length === 0 ? null : (
          <div className="sr-chips" role="group" aria-label="Highlight one category" data-testid="sr-chips">
            <button
              type="button"
              className="sr-chip"
              aria-pressed={category === null}
              data-testid="sr-chip-ALL"
              onClick={() => {
                setCategory(null);
              }}
            >
              All <span className="sr-chip-count">{String(detail.counts.items)}</span>
            </button>
            {presentCategories.map((c) => (
              <button
                key={c}
                type="button"
                className="sr-chip"
                aria-pressed={category === c}
                data-testid={`sr-chip-${c}`}
                onClick={() => {
                  setCategory(category === c ? null : c);
                }}
              >
                <i aria-hidden="true" className="el-swatch" style={{ background: ELEMENT_CATEGORIES[c].colour }} />
                {ELEMENT_CATEGORIES[c].label}
                <span className="sr-chip-count">{String(detail.counts.byCategory[c] ?? 0)}</span>
              </button>
            ))}
          </div>
        )}

        {highlights && presentCategories.length > 0 ? <MarkKey open={keyOpen} onToggle={setKeyOpen} /> : null}
      </header>

      <div className="sr-scroll" ref={scroller}>
        {scene.text === null || scene.text.trim() === '' ? (
          <p className="sr-empty" data-testid="sr-no-text">
            This scene has no text under its heading.
          </p>
        ) : (
          <div
            ref={body}
            className="sr-page"
            data-testid="sr-text"
            data-highlights={highlights ? 'on' : 'off'}
            style={{ fontSize: FONT_STEPS[fontStep] ?? FONT_STEPS[DEFAULT_FONT_STEP] }}
            onMouseUp={captureSelection}
          >
            {lines.map((line) => (
              <p key={line.index} className="sr-line" data-kind={line.kind}>
                {line.kind === 'BLANK' ? ' ' : renderRuns(text, lineSegments(line, textRuns))}
                {/* The line break, kept in the text a selection is measured against, never shown. */}
                <span className="sr-nl" aria-hidden="true">
                  {'\n'}
                </span>
              </p>
            ))}
          </div>
        )}
      </div>

      <div className="sr-toolbar" data-testid="sr-toolbar">
        <button
          type="button"
          className="ctl sr-tool-text"
          aria-pressed={highlights}
          data-testid="sr-highlights"
          onClick={() => {
            setHighlights((on) => !on);
          }}
        >
          <Icon name={highlights ? 'eye' : 'eyeOff'} size={15} />
          {highlights ? 'Highlights on' : 'Highlights off'}
        </button>
        <span className="sr-toolbar-hint" data-testid="sr-tag-hint">
          <Icon name="tag" size={14} />
          {refusal === null ? 'Select words to tag them' : 'Read-only for your role'}
        </span>
        <span className="sr-toolbar-end" role="group" aria-label="Text size">
          <button
            type="button"
            className="ctl sr-tool"
            data-testid="sr-font-down"
            aria-label="Smaller text"
            disabled={fontStep === 0}
            onClick={() => {
              setFontStep((n) => Math.max(0, n - 1));
            }}
          >
            A−
          </button>
          <button
            type="button"
            className="ctl sr-tool"
            data-testid="sr-font-up"
            aria-label="Larger text"
            disabled={fontStep === FONT_STEPS.length - 1}
            onClick={() => {
              setFontStep((n) => Math.min(FONT_STEPS.length - 1, n + 1));
            }}
          >
            A+
          </button>
        </span>
      </div>

      {overlap === null ? null : (
        <div
          className="sr-popover sr-overlap"
          role="dialog"
          aria-label="Items marked here"
          data-testid="sr-overlap"
          style={{ top: overlap.top, left: overlap.left }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setOverlap(null);
          }}
        >
          <div className="sr-popover-head">
            <span className="sr-popover-sub">{`${String(overlap.keys.length)} items marked here`}</span>
            <button type="button" className="sr-popover-close" aria-label="Close" onClick={() => setOverlap(null)}>
              <Icon name="close" size={14} />
            </button>
          </div>
          <ul className="sr-overlap-list">
            {overlap.keys.map((key) => {
              const item = byKey.get(key);
              if (item === undefined) return null;

              return (
                <li key={key}>
                  <button
                    type="button"
                    className="sr-overlap-item"
                    data-testid={`sr-overlap-${key}`}
                    onClick={() => {
                      setOverlap(null);
                      onPickItem(key);
                    }}
                  >
                    <i aria-hidden="true" className="el-swatch" style={{ background: ELEMENT_CATEGORIES[item.category].colour }} />
                    <span>
                      <strong>{item.name}</strong>
                      <span className="sr-overlap-meta">
                        {`${ELEMENT_CATEGORIES[item.category].label} · ${ITEM_STATE_LABEL[item.state]}`}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {tagging === null ? null : (
        <TagPopover
          text={tagging.text}
          top={tagging.top}
          left={tagging.left}
          disabledReason={refusal}
          onClose={() => {
            setTagging(null);
          }}
          onTag={(c, scope) => {
            setTagging(null);
            window.getSelection()?.removeAllRanges();
            onTag(tagging.text, c, scope);
          }}
        />
      )}
    </div>
  );
}

/** One marked stretch of text: drawn as its primary item, naming every item it covers. */
function MarkSpan({
  run,
  words,
  items,
  selectedKey,
  onPick,
}: {
  run: MarkRun;
  words: string;
  items: ReadonlyMap<string, SceneItemView>;
  selectedKey: string | null;
  onPick: (run: MarkRun, event: MouseEvent<HTMLElement>) => void;
}) {
  const keys = run.keys.filter((k) => k !== FLASH);
  const flashing = run.keys.includes(FLASH);

  // Only the pulse covers this: drawn as a pulse, nothing to pick.
  if (keys.length === 0) {
    return (
      <span className="sr-flash" data-flash="1">
        {words}
      </span>
    );
  }

  // The pulse never stands in for an item: the first real item covering the run is drawn.
  const primaryKey = keys[0] as string;
  const primary = items.get(primaryKey);
  const category = primary?.category ?? run.primary.category;
  const colour = ELEMENT_CATEGORIES[category].colour;
  const style = run.primary.key === FLASH && primary !== undefined ? markStyleOf(primary) : run.primary.style;
  const others = keys.length - 1;
  const label =
    primary === undefined
      ? 'A marked item'
      : `${ELEMENT_CATEGORIES[primary.category].label}: ${primary.name} — ${ITEM_STATE_LABEL[primary.state]}, ${evidenceLabel(primary.evidence)}${others > 0 ? `, and ${String(others)} more` : ''}`;

  return (
    <mark
      className="sr-mark"
      data-style={style}
      data-key={primaryKey}
      data-category={category}
      data-selected={keys.includes(selectedKey ?? '') ? '1' : '0'}
      data-overlap={others > 0 ? String(keys.length) : undefined}
      data-flash={flashing ? '1' : undefined}
      data-testid={`sr-mark-${primaryKey}`}
      title={label}
      style={markPaint(style, colour)}
      onClick={(event) => {
        onPick(run, event);
      }}
    >
      {words}
      {others > 0 ? (
        <sup className="sr-mark-more" aria-hidden="true">
          {`+${String(others)}`}
        </sup>
      ) : null}
    </mark>
  );
}

interface ReaderMenuProps {
  projectId: string;
  sceneId: string;
  /** Actions the workspace adds for this scene (the paid re-read), after the reader's own. */
  extra: readonly MenuItem[];
}

/** Copy a link to the scene; open it among the scenes; whatever the workspace adds. */
function ReaderMenu(props: ReaderMenuProps) {
  const routed = useInRouterContext();

  return routed ? <RoutedReaderMenu {...props} /> : <PlainReaderMenu {...props} />;
}

function RoutedReaderMenu(props: ReaderMenuProps) {
  const navigate = useNavigate();

  return (
    <ReaderMenuItems
      {...props}
      open={(href) => {
        void navigate(href);
      }}
    />
  );
}

function PlainReaderMenu(props: ReaderMenuProps) {
  return (
    <ReaderMenuItems
      {...props}
      open={(href) => {
        window.location.assign(href);
      }}
    />
  );
}

function ReaderMenuItems({ projectId, sceneId, extra, open }: ReaderMenuProps & { open: (href: string) => void }) {
  const [copied, setCopied] = useState<'yes' | 'no' | null>(null);

  useEffect(() => {
    if (copied === null) return;
    const timer = window.setTimeout(() => {
      setCopied(null);
    }, 2400);

    return () => {
      window.clearTimeout(timer);
    };
  }, [copied]);

  const items: MenuItem[] = [
    {
      id: 'copy',
      label: 'Copy link to this scene',
      testId: 'sr-copy-link',
      onSelect: () => {
        const url = new URL(window.location.href);
        url.searchParams.set('scene', sceneId);
        url.searchParams.delete('item');
        const write = navigator.clipboard?.writeText(url.toString());
        if (write === undefined) {
          setCopied('no');
          return;
        }
        write.then(
          () => {
            setCopied('yes');
          },
          () => {
            setCopied('no');
          },
        );
      },
    },
    {
      id: 'scenes',
      label: 'Open in Scenes',
      hint: 'The scene’s heading, text and confirmation',
      testId: 'sr-open-in-scenes',
      onSelect: () => {
        open(`/projects/${projectId}/scenes?scene=${encodeURIComponent(sceneId)}`);
      },
    },
    ...extra,
  ];

  return (
    <>
      <MoreMenu items={items} label="More for this scene" testId="sr-more" />
      {copied === null ? null : (
        <span className="sr-copied" role="status" data-testid="sr-copied">
          {copied === 'yes' ? 'Link copied' : 'Could not copy the link'}
        </span>
      )}
    </>
  );
}
